/**
 * Protocolos guiados de trabajo — la app como método, no como formato.
 *
 * La intención de fondo de la plataforma (hoja de ruta, punto 7): que la Central
 * GUÍE a quien está parado en la casa por el modo de trabajo correcto. El ejemplo
 * que define todo: un apagador inteligente murió → el protocolo NO es "cambiar el
 * apagador", es la revisión que encuentra la CAUSA de la muerte — porque sustituir
 * sin diagnóstico produce un apagador que vuelve a morir, una queja, y una
 * garantía pagada dos veces.
 *
 * El CONTENIDO vive en el catálogo (`catalogoProtocolos`, editable como los
 * factores, sin tocar código); aquí viven la forma y la semilla. Un protocolo:
 *
 *   { id, nombre, cuando, herr: [idsHerramienta],
 *     guionLlegada: [preguntas al cliente al llegar],
 *     pasos: [{ id, titulo, instruccion, tipo: 'medicion'|'verificacion',
 *               unidad?, criterio }],
 *     guionCierre: [qué decirle al cliente al cerrar] }
 *
 * El protocolo TERMINA en una intervención de la bitácora (punto 6): las
 * respuestas y mediciones quedan registradas — el registro prueba que el método
 * se siguió, y es defensa de garantía y control de calidad al mismo tiempo.
 *
 * El primer protocolo es el de la Revisión: los 5 pasos publicados en
 * `Web/sohamiot-web/electrico.html` («Medimos antes de proponer») con sus
 * aparatos. Los UMBRALES numéricos de aceptación no se siembran a propósito:
 * son dato de norma o criterio de Juan, y se capturan en el catálogo con su
 * fuente — nunca un número inventado por defecto.
 */

/* Las tres herramientas que la Revisión exige (hoja de ruta, punto 8). Ids
   fijos para que la semilla sea idempotente y el enlace no dependa del orden. */
export const HERRAMIENTAS_SEMILLA = [
  { id: 'megohmetro-500', nombre: 'Megóhmetro 500 V CD', descripcion: 'Prueba de resistencia de aislamiento', activa: true },
  { id: 'camara-termografica', nombre: 'Cámara termográfica', descripcion: 'Termografía del centro de carga', activa: true },
  { id: 'pinza-trms', nombre: 'Pinza de corriente de valor eficaz verdadero', descripcion: 'Tensión y corriente por circuito', activa: true }
]

export const PROTOCOLO_REVISION = {
  id: 'revision-electrica',
  nombre: 'La Revisión — diagnóstico eléctrico de la casa',
  cuando: 'Revisión vendida (servicio de 600), o cualquier falla eléctrica reportada: ' +
          'un aparato que murió, pastillas que se botan, apagadores que calientan. ' +
          'La regla de la casa: causa raíz — no se cambia la pieza sin saber qué la mató.',
  herr: HERRAMIENTAS_SEMILLA.map(h => h.id),
  guionLlegada: [
    '¿Qué falla ha notado y desde cuándo?',
    '¿Se ha modificado la instalación? (ampliaciones, minisplit, tinaco, cargador de auto…)',
    '¿Hay contactos o apagadores que calienten, huelan o truenen?',
    '¿Se botan las pastillas? ¿Cuáles y en qué circunstancia?'
  ],
  pasos: [
    {
      id: 'aislamiento',
      titulo: 'Resistencia de aislamiento',
      instruccion: 'Entre fase y tierra, y entre neutro y tierra, por circuito. Determina si ' +
                   'existe fuga de corriente y en qué circuito está.',
      tipo: 'medicion',
      unidad: 'MΩ por circuito',
      criterio: 'Registra el valor de cada circuito. Un aislamiento degradado ES la fuga: ' +
                'localízala antes de proponer nada. Umbral de aceptación: capturarlo aquí ' +
                'con su fuente (NOM-001-SEDE / criterio propio) — pendiente de fijar.'
    },
    {
      id: 'termografia',
      titulo: 'Termografía del centro de carga',
      instruccion: 'Con carga conectada. Convierte un riesgo invisible en una fotografía que ' +
                   'el cliente ve en su pantalla durante la visita — tómala y muéstrasela.',
      tipo: 'verificacion',
      criterio: '¿Algún punto notablemente más caliente que sus vecinos? Un borne caliente es ' +
                'conexión floja o sobrecarga: foto térmica al expediente y a la corrección.'
    },
    {
      id: 'carga-circuitos',
      titulo: 'Tensión y corriente por circuito',
      instruccion: 'Carga REAL de cada circuito con la pinza, no la carga supuesta. Es lo que ' +
                   'revela un conductor trabajando por encima de su capacidad.',
      tipo: 'medicion',
      unidad: 'V y A por circuito',
      criterio: 'Compara la corriente medida contra el calibre instalado y su protección. ' +
                'Un conductor al límite hoy es la falla de mañana: va al dictamen.'
    },
    {
      id: 'tierra',
      titulo: 'Continuidad de tierra física',
      instruccion: 'Comprueba que la trayectoria de tierra existe y conduce hasta los ' +
                   'contactos. Una varilla enterrada sin continuidad no protege a nadie.',
      tipo: 'verificacion',
      criterio: '¿Conduce de la varilla al último contacto? Sin tierra continua, ningún ' +
                'equipo electrónico queda protegido — y las aseguradoras lo exigen como ' +
                'condición de cobertura (cláusula BBVA verificada).'
    },
    {
      id: 'dictamen',
      titulo: 'Dictamen en sitio, con precio cerrado',
      instruccion: 'El cliente se queda con el documento en la mano: qué se midió, qué se ' +
                   'encontró, la referencia normativa aplicable y el costo CERRADO de la ' +
                   'corrección. Sin cotización abierta ni ajustes posteriores.',
      tipo: 'verificacion',
      criterio: '¿Documento entregado y explicado? La visita no termina cuando acabas de ' +
                'medir: termina cuando el cliente entendió qué tiene y qué le propones.'
    }
  ],
  guionCierre: [
    'Enséñale lo medido en pantalla: la termografía y los valores, no adjetivos.',
    'Qué cubre el dictamen y qué no — dicho aquí, no discutido después.',
    'Si hay corrección: precio cerrado y qué garantía emite ese trabajo.',
    'La regla de la casa, dicha en voz alta: corregimos la causa, no el síntoma.'
  ]
}
